import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getSekolahList, createSekolah, updateSekolah, getSekolahDetail, createUser } from '../../lib/api';
import { formatDate, formatTime } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  ChevronLeft, Plus, Edit, MapPin, Clock, Loader2, Building2, Users,
  GraduationCap, School, BookOpen, Baby, Briefcase, UserCheck, UserX,
  X, RefreshCw, UserPlus, Eye, EyeOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../ui/sonner';

// Jenjang configuration with colors and icons
const JENJANG_CONFIG = {
  'KB': { 
    label: 'KB', 
    color: 'bg-pink-100 text-pink-800 border-pink-200',
    cardBorder: 'border-l-pink-500',
    icon: Baby,
    bgGradient: 'from-pink-50 to-pink-100/50'
  },
  'TK-PAUD': { 
    label: 'TK-PAUD', 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    cardBorder: 'border-l-purple-500',
    icon: Baby,
    bgGradient: 'from-purple-50 to-purple-100/50'
  },
  'SD': { 
    label: 'SD', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    cardBorder: 'border-l-blue-500',
    icon: School,
    bgGradient: 'from-blue-50 to-blue-100/50'
  },
  'SMP': { 
    label: 'SMP', 
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    cardBorder: 'border-l-emerald-500',
    icon: BookOpen,
    bgGradient: 'from-emerald-50 to-emerald-100/50'
  },
  'SMA/Sederajat': { 
    label: 'SMA/Sederajat', 
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    cardBorder: 'border-l-amber-500',
    icon: GraduationCap,
    bgGradient: 'from-amber-50 to-amber-100/50'
  },
  'SMK': { 
    label: 'SMK', 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    cardBorder: 'border-l-orange-500',
    icon: Briefcase,
    bgGradient: 'from-orange-50 to-orange-100/50'
  }
};

const JENJANG_OPTIONS = ['KB', 'TK-PAUD', 'SD', 'SMP', 'SMA/Sederajat', 'SMK'];

// Daftar Distrik di Kabupaten Asmat
const DISTRIK_OPTIONS = [
  "Agats", "Atsj", "Sawa Erma", "Akat", "Fayit", "Pantai Kasuari", 
  "Suator", "Suru-suru", "Kolf Braza", "Unir Sirau", "Joerat", 
  "Aswi", "Ayip", "Der Koumor", "Kopay", "Safan", "Pulau Tiga", 
  "Sor Ep", "Jetsy", "Tomor Birip", "Sirets", "Betcbamu", 
  "Joutu", "Awyu", "Koroway Buluanop"
];

const SekolahPage = () => {
  const { user, isAdminDinas } = useAuth();
  const navigate = useNavigate();
  const [sekolahList, setSekolahList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSekolah, setEditingSekolah] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterJenjang, setFilterJenjang] = useState('all');
  const [filterDistrik, setFilterDistrik] = useState('all');
  const [searchKampung, setSearchKampung] = useState('');
  
  // Detail dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedSekolah, setSelectedSekolah] = useState(null);
  const [sekolahDetail, setSekolahDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Add Guru dialog state
  const [showAddGuruDialog, setShowAddGuruDialog] = useState(false);
  const [addGuruSekolah, setAddGuruSekolah] = useState(null);
  const [submittingGuru, setSubmittingGuru] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [guruFormData, setGuruFormData] = useState({
    nip: '',
    nama: '',
    email: '',
    password: '',
    role: 'guru',
    status_kepegawaian: ''
  });

  // Status Kepegawaian options
  const STATUS_KEPEGAWAIAN_OPTIONS = [
    'ASN',
    'PPPK',
    'Guru Kontrak',
    'Guru Honor Daerah',
    'Non-ASN',
    'Guru Kontrak Luar Papua'
  ];

  // Form state
  const [formData, setFormData] = useState({
    nama: '',
    alamat: '',
    jenjang: 'SD',
    latitude: '',
    longitude: '',
    radius_geofence: 100,
    jam_masuk: '07:00',
    jam_pulang: '14:00',
    toleransi_terlambat: 15,
    mode_shift: 'pagi',
    jam_masuk_siang: '12:30',
    jam_pulang_siang: '17:00',
    toleransi_terlambat_siang: 15,
    npsn: '',
    kampung: '',
    distrik: ''
  });

  useEffect(() => {
    loadSekolah();
  }, []);

  const loadSekolah = async () => {
    try {
      setLoading(true);
      const data = await getSekolahList();
      setSekolahList(data);
    } catch (error) {
      console.error('Failed to load sekolah:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius_geofence: parseInt(formData.radius_geofence),
        toleransi_terlambat: parseInt(formData.toleransi_terlambat)
      };

      if (editingSekolah) {
        await updateSekolah(editingSekolah.id, data);
        toast.success('Sekolah berhasil diperbarui');
      } else {
        await createSekolah(data);
        toast.success('Sekolah berhasil ditambahkan');
      }

      setShowDialog(false);
      resetForm();
      loadSekolah();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data sekolah');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (sekolah, e) => {
    e.stopPropagation();
    setEditingSekolah(sekolah);
    setFormData({
      nama: sekolah.nama,
      alamat: sekolah.alamat,
      jenjang: sekolah.jenjang || 'SD',
      latitude: sekolah.latitude,
      longitude: sekolah.longitude,
      radius_geofence: sekolah.radius_geofence,
      jam_masuk: sekolah.jam_masuk,
      jam_pulang: sekolah.jam_pulang,
      toleransi_terlambat: sekolah.toleransi_terlambat,
      mode_shift: sekolah.mode_shift || 'pagi',
      jam_masuk_siang: sekolah.jam_masuk_siang || '12:30',
      jam_pulang_siang: sekolah.jam_pulang_siang || '17:00',
      toleransi_terlambat_siang: sekolah.toleransi_terlambat_siang || 15,
      npsn: sekolah.npsn || '',
      kampung: sekolah.kampung || '',
      distrik: sekolah.distrik || ''
    });
    setShowDialog(true);
  };

  const openDetailDialog = async (sekolah) => {
    setSelectedSekolah(sekolah);
    setShowDetailDialog(true);
    setLoadingDetail(true);
    
    try {
      const detail = await getSekolahDetail(sekolah.id);
      setSekolahDetail(detail);
    } catch (error) {
      console.error('Failed to load detail:', error);
      toast.error('Gagal memuat detail sekolah');
    } finally {
      setLoadingDetail(false);
    }
  };

  const resetForm = () => {
    setEditingSekolah(null);
    setFormData({
      nama: '',
      alamat: '',
      jenjang: 'SD',
      latitude: '',
      longitude: '',
      radius_geofence: 100,
      jam_masuk: '07:00',
      jam_pulang: '14:00',
      toleransi_terlambat: 15,
      mode_shift: 'pagi',
      jam_masuk_siang: '12:30',
      jam_pulang_siang: '17:00',
      toleransi_terlambat_siang: 15,
      npsn: '',
      kampung: '',
      distrik: ''
    });
  };

  const getCurrentLocation = async () => {
    try {
      const { getCurrentPosition } = await import('../../lib/platformUtils');
      const position = await getCurrentPosition();
      setFormData(prev => ({
        ...prev,
        latitude: position.coords.latitude.toFixed(6),
        longitude: position.coords.longitude.toFixed(6)
      }));
      toast.success('Lokasi berhasil didapatkan');
    } catch {
      toast.error('Gagal mendapatkan lokasi');
    }
  };

  // Filter sekolah by jenjang, distrik, and kampung
  const filteredSekolah = sekolahList.filter(sekolah => {
    const matchJenjang = filterJenjang === 'all' || sekolah.jenjang === filterJenjang;
    const matchDistrik = filterDistrik === 'all' || sekolah.distrik === filterDistrik;
    const matchKampung = searchKampung === '' || 
      (sekolah.kampung && sekolah.kampung.toLowerCase().includes(searchKampung.toLowerCase())) ||
      (sekolah.nama && sekolah.nama.toLowerCase().includes(searchKampung.toLowerCase()));
    return matchJenjang && matchDistrik && matchKampung;
  });

  // Count by jenjang
  const jenjangCounts = JENJANG_OPTIONS.reduce((acc, jenjang) => {
    acc[jenjang] = sekolahList.filter(s => s.jenjang === jenjang).length;
    return acc;
  }, {});

  // Get unique distrik from sekolah list
  const availableDistrik = [...new Set(sekolahList.map(s => s.distrik).filter(Boolean))].sort();

  const getJenjangConfig = (jenjang) => {
    return JENJANG_CONFIG[jenjang] || JENJANG_CONFIG['SD'];
  };

  // Handle Guru Form Input
  const handleGuruInputChange = (e) => {
    const { name, value } = e.target;
    setGuruFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGuruSelectChange = (name, value) => {
    setGuruFormData(prev => ({ ...prev, [name]: value }));
  };

  // Reset Guru Form
  const resetGuruForm = () => {
    setGuruFormData({
      nip: '',
      nama: '',
      email: '',
      password: '',
      role: 'guru',
      status_kepegawaian: ''
    });
    setShowPassword(false);
  };

  // Open Add Guru Dialog
  const openAddGuruDialog = (sekolah, e) => {
    if (e) e.stopPropagation();
    setAddGuruSekolah(sekolah);
    resetGuruForm();
    setShowAddGuruDialog(true);
  };

  // Submit Add Guru
  const handleAddGuruSubmit = async (e) => {
    e.preventDefault();
    
    if (!addGuruSekolah) {
      toast.error('Pilih sekolah terlebih dahulu');
      return;
    }

    // Validate NIP - must be numeric and at least 8 characters
    if (!/^\d{8,}$/.test(guruFormData.nip)) {
      toast.error('NIP harus berupa angka minimal 8 digit');
      return;
    }

    // Validate password strength
    if (guruFormData.password.length < 8) {
      toast.error('Password minimal 8 karakter');
      return;
    }
    if (!/[A-Z]/.test(guruFormData.password)) {
      toast.error('Password harus mengandung huruf besar');
      return;
    }
    if (!/[a-z]/.test(guruFormData.password)) {
      toast.error('Password harus mengandung huruf kecil');
      return;
    }
    if (!/[0-9]/.test(guruFormData.password)) {
      toast.error('Password harus mengandung angka');
      return;
    }

    setSubmittingGuru(true);

    try {
      const userData = {
        nip: guruFormData.nip,
        nama: guruFormData.nama,
        email: guruFormData.email || null,
        password: guruFormData.password,
        role: guruFormData.role,
        sekolah_id: addGuruSekolah.id,
        status_kepegawaian: guruFormData.status_kepegawaian || null
      };

      await createUser(userData);
      const roleLabels = { guru: 'Guru', kepala_sekolah: 'Kepala Sekolah', tenaga_tata_usaha: 'Tenaga Tata Usaha' };
      toast.success(`${roleLabels[guruFormData.role] || 'User'} berhasil ditambahkan ke ${addGuruSekolah.nama}`);
      
      setShowAddGuruDialog(false);
      resetGuruForm();
      
      // Refresh detail if dialog is open
      if (showDetailDialog && selectedSekolah?.id === addGuruSekolah.id) {
        const detail = await getSekolahDetail(addGuruSekolah.id);
        setSekolahDetail(detail);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Gagal menambahkan user';
      toast.error(msg);
    } finally {
      setSubmittingGuru(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="sekolah-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950">Kelola Sekolah</h1>
            <p className="text-gray-500">Manajemen data sekolah dan geofencing</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadSekolah} className="border-indigo-200">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {isAdminDinas && (
            <Dialog open={showDialog} onOpenChange={(open) => {
              setShowDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-950 hover:bg-indigo-900" data-testid="add-sekolah-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Sekolah
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingSekolah ? 'Edit Sekolah' : 'Tambah Sekolah Baru'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nama">Nama Sekolah</Label>
                    <Input
                      id="nama"
                      name="nama"
                      value={formData.nama}
                      onChange={handleInputChange}
                      placeholder="SDN 1 Contoh"
                      required
                      data-testid="sekolah-nama-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="jenjang">Jenjang Pendidikan</Label>
                    <Select 
                      value={formData.jenjang} 
                      onValueChange={(value) => handleSelectChange('jenjang', value)}
                    >
                      <SelectTrigger data-testid="jenjang-select">
                        <SelectValue placeholder="Pilih Jenjang" />
                      </SelectTrigger>
                      <SelectContent>
                        {JENJANG_OPTIONS.map(jenjang => {
                          const config = getJenjangConfig(jenjang);
                          const Icon = config.icon;
                          return (
                            <SelectItem key={jenjang} value={jenjang}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {jenjang}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="npsn">NPSN</Label>
                    <Input
                      id="npsn"
                      name="npsn"
                      value={formData.npsn}
                      onChange={handleInputChange}
                      placeholder="Nomor Pokok Sekolah Nasional"
                      data-testid="npsn-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="distrik">Distrik</Label>
                      <Select 
                        value={formData.distrik} 
                        onValueChange={(value) => handleSelectChange('distrik', value)}
                      >
                        <SelectTrigger data-testid="distrik-select">
                          <SelectValue placeholder="Pilih Distrik" />
                        </SelectTrigger>
                        <SelectContent>
                          {DISTRIK_OPTIONS.map(distrik => (
                            <SelectItem key={distrik} value={distrik}>
                              {distrik}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="kampung">Kampung</Label>
                      <Input
                        id="kampung"
                        name="kampung"
                        value={formData.kampung}
                        onChange={handleInputChange}
                        placeholder="Nama Kampung"
                        data-testid="kampung-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="alamat">Alamat</Label>
                    <Input
                      id="alamat"
                      name="alamat"
                      value={formData.alamat}
                      onChange={handleInputChange}
                      placeholder="Jl. Pendidikan No. 1"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="latitude">Latitude</Label>
                      <Input
                        id="latitude"
                        name="latitude"
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        placeholder="-6.12345"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="longitude">Longitude</Label>
                      <Input
                        id="longitude"
                        name="longitude"
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        placeholder="106.12345"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={getCurrentLocation}
                    className="w-full border-indigo-200"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Gunakan Lokasi Saat Ini
                  </Button>

                  <div className="space-y-2">
                    <Label htmlFor="radius_geofence">Radius Geofence (meter)</Label>
                    <Input
                      id="radius_geofence"
                      name="radius_geofence"
                      type="number"
                      value={formData.radius_geofence}
                      onChange={handleInputChange}
                      placeholder="100"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="jam_masuk">Jam Masuk</Label>
                      <Input
                        id="jam_masuk"
                        name="jam_masuk"
                        type="time"
                        value={formData.jam_masuk}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jam_pulang">Jam Pulang</Label>
                      <Input
                        id="jam_pulang"
                        name="jam_pulang"
                        type="time"
                        value={formData.jam_pulang}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="toleransi_terlambat">Toleransi Terlambat (menit)</Label>
                    <Input
                      id="toleransi_terlambat"
                      name="toleransi_terlambat"
                      type="number"
                      value={formData.toleransi_terlambat}
                      onChange={handleInputChange}
                      placeholder="15"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Mode Shift</Label>
                    <select
                      name="mode_shift"
                      value={formData.mode_shift}
                      onChange={handleInputChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="mode-shift-select"
                    >
                      <option value="pagi">Pagi Saja</option>
                      <option value="pagi_siang">Pagi & Siang (2 Shift)</option>
                    </select>
                  </div>

                  {formData.mode_shift === 'pagi_siang' && (
                    <>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm font-medium text-blue-800 mb-3">Shift Siang</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="jam_masuk_siang" className="text-xs">Jam Masuk Siang</Label>
                            <Input
                              id="jam_masuk_siang"
                              name="jam_masuk_siang"
                              type="time"
                              value={formData.jam_masuk_siang}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="jam_pulang_siang" className="text-xs">Jam Pulang Siang</Label>
                            <Input
                              id="jam_pulang_siang"
                              name="jam_pulang_siang"
                              type="time"
                              value={formData.jam_pulang_siang}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                        <div className="space-y-2 mt-3">
                          <Label htmlFor="toleransi_terlambat_siang" className="text-xs">Toleransi Terlambat Siang (menit)</Label>
                          <Input
                            id="toleransi_terlambat_siang"
                            name="toleransi_terlambat_siang"
                            type="number"
                            value={formData.toleransi_terlambat_siang}
                            onChange={handleInputChange}
                            placeholder="15"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowDialog(false);
                        resetForm();
                      }}
                      className="flex-1"
                    >
                      Batal
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-indigo-950"
                      data-testid="submit-sekolah-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        editingSekolah ? 'Simpan Perubahan' : 'Tambah Sekolah'
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-6 space-y-4">
        {/* Distrik & Kampung Search */}
        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Filter Distrik</Label>
                <Select value={filterDistrik} onValueChange={setFilterDistrik}>
                  <SelectTrigger data-testid="filter-distrik-select">
                    <SelectValue placeholder="Semua Distrik" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Distrik</SelectItem>
                    {DISTRIK_OPTIONS.map(distrik => (
                      <SelectItem key={distrik} value={distrik}>{distrik}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Cari Kampung / Nama Sekolah</Label>
                <Input
                  placeholder="Ketik nama kampung atau sekolah..."
                  value={searchKampung}
                  onChange={(e) => setSearchKampung(e.target.value)}
                  data-testid="search-kampung-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilterDistrik('all');
                    setSearchKampung('');
                    setFilterJenjang('all');
                  }}
                  className="w-full border-indigo-200"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Filter
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Jenjang Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filterJenjang === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterJenjang('all')}
            className={filterJenjang === 'all' ? 'bg-indigo-950' : 'border-indigo-200'}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Semua ({sekolahList.length})
          </Button>
          {JENJANG_OPTIONS.map(jenjang => {
            const config = getJenjangConfig(jenjang);
            const Icon = config.icon;
            const count = jenjangCounts[jenjang] || 0;
            return (
              <Button
                key={jenjang}
                variant={filterJenjang === jenjang ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterJenjang(jenjang)}
                className={filterJenjang === jenjang ? 'bg-indigo-950' : `border-indigo-200 ${count === 0 ? 'opacity-50' : ''}`}
                disabled={count === 0}
              >
                <Icon className="w-4 h-4 mr-2" />
                {jenjang} ({count})
              </Button>
            );
          })}
        </div>

        {/* Active Filters Summary */}
        {(filterDistrik !== 'all' || searchKampung || filterJenjang !== 'all') && (
          <div className="mt-3 text-sm text-gray-600">
            Menampilkan <span className="font-semibold text-indigo-700">{filteredSekolah.length}</span> dari {sekolahList.length} sekolah
            {filterDistrik !== 'all' && <span> di <span className="font-medium">Distrik {filterDistrik}</span></span>}
            {searchKampung && <span> yang cocok dengan "<span className="font-medium">{searchKampung}</span>"</span>}
          </div>
        )}
      </div>

      {/* Sekolah List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredSekolah.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">
            {filterJenjang === 'all' && filterDistrik === 'all' && !searchKampung 
              ? 'Belum ada data sekolah' 
              : 'Tidak ada sekolah yang cocok dengan filter'}
          </p>
          <p className="text-sm">
            {filterJenjang === 'all' && filterDistrik === 'all' && !searchKampung 
              ? 'Klik tombol "Tambah Sekolah" untuk menambahkan' 
              : 'Ubah filter atau coba kata kunci lain'}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSekolah.map((sekolah) => {
            const config = getJenjangConfig(sekolah.jenjang || 'SD');
            const Icon = config.icon;
            return (
              <Card 
                key={sekolah.id} 
                className={`border-indigo-100/50 card-hover border-l-4 ${config.cardBorder} cursor-pointer transition-all hover:shadow-lg`}
                onClick={() => openDetailDialog(sekolah)}
              >
                <CardHeader className={`pb-2 bg-gradient-to-r ${config.bgGradient} rounded-tr-lg`}>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color.replace('text-', 'bg-').split(' ')[0]}`}>
                        <Icon className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-lg font-semibold truncate block text-indigo-950 hover:text-indigo-700">
                          {sekolah.nama}
                        </span>
                        <Badge className={`${config.color} text-xs`}>
                          {sekolah.jenjang || 'SD'}
                        </Badge>
                      </div>
                    </div>
                    {isAdminDinas && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => openAddGuruDialog(sekolah, e)}
                          title="Tambah Guru"
                          data-testid={`add-guru-${sekolah.id}`}
                        >
                          <UserPlus className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => openEditDialog(sekolah, e)}
                          data-testid={`edit-sekolah-${sekolah.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3">
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{sekolah.alamat}</p>
                  
                  <div className="space-y-2 text-sm">
                    {sekolah.npsn && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-medium">NPSN:</span>
                        <span className="font-mono">{sekolah.npsn}</span>
                      </div>
                    )}
                    {sekolah.distrik && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-medium">Distrik:</span>
                        <span>{sekolah.distrik}</span>
                        {sekolah.kampung && <span className="text-gray-400">• {sekolah.kampung}</span>}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-gray-500">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="font-mono text-xs truncate">
                        {sekolah.latitude.toFixed(6)}, {sekolah.longitude.toFixed(6)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-500">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      <span>{sekolah.jam_masuk} - {sekolah.jam_pulang}</span>
                      {sekolah.mode_shift === 'pagi_siang' && sekolah.jam_masuk_siang && (
                        <span className="text-blue-600">| {sekolah.jam_masuk_siang} - {sekolah.jam_pulang_siang}</span>
                      )}
                    </div>
                    
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {sekolah.mode_shift === 'pagi_siang' && (
                        <Badge className="bg-blue-100 text-blue-800">2 Shift</Badge>
                      )}
                      <Badge variant="outline" className="border-indigo-200">
                        Radius {sekolah.radius_geofence}m
                      </Badge>
                      <Badge variant="outline" className="border-amber-200">
                        Toleransi {sekolah.toleransi_terlambat} menit
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedSekolah && (
                <>
                  {(() => {
                    const config = getJenjangConfig(selectedSekolah.jenjang || 'SD');
                    const Icon = config.icon;
                    return (
                      <>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color.replace('text-', 'bg-').split(' ')[0]}`}>
                          <Icon className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                        </div>
                        <div>
                          <span className="text-xl">{selectedSekolah.nama}</span>
                          <Badge className={`${config.color} ml-2`}>
                            {selectedSekolah.jenjang || 'SD'}
                          </Badge>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : sekolahDetail ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-indigo-100/50">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
                    <p className="text-2xl font-bold text-indigo-950">{sekolahDetail.summary.total_guru}</p>
                    <p className="text-sm text-gray-500">Total Guru</p>
                  </CardContent>
                </Card>
                <Card className="border-emerald-100/50">
                  <CardContent className="p-4 text-center">
                    <UserCheck className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                    <p className="text-2xl font-bold text-emerald-600">{sekolahDetail.summary.sudah_absen_hari_ini}</p>
                    <p className="text-sm text-gray-500">Sudah Absen</p>
                  </CardContent>
                </Card>
                <Card className="border-red-100/50">
                  <CardContent className="p-4 text-center">
                    <UserX className="w-8 h-8 mx-auto mb-2 text-red-600" />
                    <p className="text-2xl font-bold text-red-600">{sekolahDetail.summary.belum_absen_hari_ini}</p>
                    <p className="text-sm text-gray-500">Belum Absen</p>
                  </CardContent>
                </Card>
              </div>

              {/* Guru Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg text-indigo-950">Daftar Guru & Absensi</h3>
                  {isAdminDinas && (
                    <Button
                      size="sm"
                      onClick={() => openAddGuruDialog(selectedSekolah)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="add-guru-from-detail-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Tambah Guru
                    </Button>
                  )}
                </div>
                {sekolahDetail.guru_list.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p className="mb-2">Belum ada guru terdaftar di sekolah ini</p>
                    {isAdminDinas && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAddGuruDialog(selectedSekolah)}
                        className="border-emerald-300 text-emerald-700"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Tambah Guru Pertama
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>NIP</th>
                          <th>Nama Guru</th>
                          <th>Status Hari Ini</th>
                          <th>Rekap Bulan Ini</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sekolahDetail.guru_list.map(guru => (
                          <tr key={guru.id}>
                            <td className="font-mono text-sm">{guru.nip}</td>
                            <td className="font-medium">{guru.nama}</td>
                            <td>
                              <div className="flex gap-2">
                                <Badge className={guru.sudah_absen_masuk 
                                  ? 'bg-emerald-100 text-emerald-800' 
                                  : 'bg-gray-100 text-gray-600'}>
                                  Masuk: {guru.sudah_absen_masuk ? '✓' : '-'}
                                </Badge>
                                <Badge className={guru.sudah_absen_pulang 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-gray-100 text-gray-600'}>
                                  Pulang: {guru.sudah_absen_pulang ? '✓' : '-'}
                                </Badge>
                              </div>
                            </td>
                            <td>
                              <div className="flex gap-1 text-xs">
                                <Badge className="bg-emerald-100 text-emerald-800">
                                  {guru.rekap_bulan_ini.hadir_tepat_waktu} Tepat
                                </Badge>
                                <Badge className="bg-amber-100 text-amber-800">
                                  {guru.rekap_bulan_ini.hadir_terlambat} Terlambat
                                </Badge>
                                <Badge className="bg-red-100 text-red-800">
                                  {guru.rekap_bulan_ini.mangkir} Mangkir
                                </Badge>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* School Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-indigo-950">Informasi Sekolah</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {sekolahDetail.sekolah.npsn && (
                    <div>
                      <span className="text-gray-500">NPSN:</span>
                      <p className="font-mono font-medium">{sekolahDetail.sekolah.npsn}</p>
                    </div>
                  )}
                  {sekolahDetail.sekolah.distrik && (
                    <div>
                      <span className="text-gray-500">Distrik:</span>
                      <p className="font-medium">{sekolahDetail.sekolah.distrik}</p>
                    </div>
                  )}
                  {sekolahDetail.sekolah.kampung && (
                    <div>
                      <span className="text-gray-500">Kampung:</span>
                      <p className="font-medium">{sekolahDetail.sekolah.kampung}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Alamat:</span>
                    <p className="font-medium">{sekolahDetail.sekolah.alamat}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Koordinat:</span>
                    <p className="font-mono text-xs">
                      {sekolahDetail.sekolah.latitude.toFixed(6)}, {sekolahDetail.sekolah.longitude.toFixed(6)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Jam Operasional (Shift Pagi):</span>
                    <p className="font-medium">{sekolahDetail.sekolah.jam_masuk} - {sekolahDetail.sekolah.jam_pulang}</p>
                  </div>
                  {sekolahDetail.sekolah.mode_shift === 'pagi_siang' && sekolahDetail.sekolah.jam_masuk_siang && (
                    <div>
                      <span className="text-gray-500">Jam Operasional (Shift Siang):</span>
                      <p className="font-medium">{sekolahDetail.sekolah.jam_masuk_siang} - {sekolahDetail.sekolah.jam_pulang_siang}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Radius Geofence:</span>
                    <p className="font-medium">{sekolahDetail.sekolah.radius_geofence} meter</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Add Guru Dialog */}
      <Dialog open={showAddGuruDialog} onOpenChange={(open) => {
        setShowAddGuruDialog(open);
        if (!open) resetGuruForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Tambah Guru/Kepala Sekolah/TU
            </DialogTitle>
          </DialogHeader>
          
          {addGuruSekolah && (
            <div className="bg-indigo-50 p-3 rounded-lg mb-4">
              <p className="text-sm text-indigo-800">
                <strong>Sekolah:</strong> {addGuruSekolah.nama}
              </p>
              <p className="text-xs text-indigo-600">
                {addGuruSekolah.jenjang} • {addGuruSekolah.alamat}
              </p>
            </div>
          )}

          <form onSubmit={handleAddGuruSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="guru-role">Jabatan</Label>
              <Select 
                value={guruFormData.role} 
                onValueChange={(value) => handleGuruSelectChange('role', value)}
              >
                <SelectTrigger data-testid="guru-role-select">
                  <SelectValue placeholder="Pilih Jabatan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="guru">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Guru
                    </div>
                  </SelectItem>
                  <SelectItem value="kepala_sekolah">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4" />
                      Kepala Sekolah
                    </div>
                  </SelectItem>
                  <SelectItem value="tenaga_tata_usaha">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Tenaga Tata Usaha
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="guru-nip">NIP / NUPTK</Label>
              <Input
                id="guru-nip"
                name="nip"
                value={guruFormData.nip}
                onChange={handleGuruInputChange}
                placeholder="198501012010011001"
                required
                data-testid="guru-nip-input"
              />
              <p className="text-xs text-gray-500">Minimal 8 digit angka, digunakan untuk login</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="guru-nama">Nama Lengkap</Label>
              <Input
                id="guru-nama"
                name="nama"
                value={guruFormData.nama}
                onChange={handleGuruInputChange}
                placeholder="Budi Santoso, S.Pd"
                required
                data-testid="guru-nama-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="guru-email">Email (Opsional)</Label>
              <Input
                id="guru-email"
                name="email"
                type="email"
                value={guruFormData.email}
                onChange={handleGuruInputChange}
                placeholder="budi@example.com"
                data-testid="guru-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="guru-status-kepegawaian">Status Kepegawaian</Label>
              <Select 
                value={guruFormData.status_kepegawaian} 
                onValueChange={(value) => handleGuruSelectChange('status_kepegawaian', value)}
              >
                <SelectTrigger data-testid="guru-status-kepegawaian-select">
                  <SelectValue placeholder="Pilih Status Kepegawaian" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_KEPEGAWAIAN_OPTIONS.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="guru-password">Password</Label>
              <div className="relative">
                <Input
                  id="guru-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={guruFormData.password}
                  onChange={handleGuruInputChange}
                  placeholder="Min. 8 karakter"
                  required
                  className="pr-10"
                  data-testid="guru-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500">Harus mengandung huruf besar, huruf kecil, dan angka</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddGuruDialog(false);
                  resetGuruForm();
                }}
                className="flex-1"
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={submittingGuru}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="submit-guru-btn"
              >
                {submittingGuru ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Tambah {guruFormData.role === 'guru' ? 'Guru' : guruFormData.role === 'kepala_sekolah' ? 'Kepala Sekolah' : 'Tenaga TU'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SekolahPage;
